import axios from 'axios'

const STORAGE_KEY = 'frontend_api_base_url'

// Use configured API URL in environment, otherwise use backend dev URL in dev mode.
// In production, build against the same origin and use /api path.
export const DEFAULT_API_URL = import.meta.env.VITE_API_URL || '/api'

if (typeof window !== 'undefined') {
  const storedApiUrl = window.localStorage.getItem(STORAGE_KEY)
  if (storedApiUrl && storedApiUrl !== DEFAULT_API_URL) {
    window.localStorage.removeItem(STORAGE_KEY)
  }
}

const initialBaseUrl = DEFAULT_API_URL
const AUTH_REFRESH_PATH = '/auth/refresh'
const AUTH_LOGIN_PATH = '/auth/login'
const AUTH_REGISTER_PATH = '/auth/register'
const REFRESH_TOKEN_KEY = 'refresh_token'
const CSRF_COOKIE_KEY = 'csrf_token'
const AUTH_TOKEN_KEY = 'auth_token'

let inMemoryAccessToken = null

export const apiClient = axios.create({
  baseURL: initialBaseUrl,
  timeout: 600000, // 10 minutes
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

function isAuthRoute(url) {
  if (!url) return false
  return url.includes(AUTH_REFRESH_PATH) || url.includes(AUTH_LOGIN_PATH) || url.includes(AUTH_REGISTER_PATH)
}

export function setAccessToken(token) {
  inMemoryAccessToken = typeof token === 'string' && token.trim() ? token.trim() : null
  if (inMemoryAccessToken) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, inMemoryAccessToken)
    localStorage.removeItem(AUTH_TOKEN_KEY)
  } else {
    sessionStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_TOKEN_KEY)
  }
}

export function getAccessToken() {
  if (inMemoryAccessToken) return inMemoryAccessToken
  const fromSession = sessionStorage.getItem(AUTH_TOKEN_KEY)
  if (fromSession && fromSession !== '[object Object]') {
    inMemoryAccessToken = fromSession
    return inMemoryAccessToken
  }
  return null
}

export function clearSessionTokens() {
  inMemoryAccessToken = null
  sessionStorage.removeItem(AUTH_TOKEN_KEY)
  localStorage.removeItem(AUTH_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

function getRefreshToken() {
  // Legacy fallback during migration; cookie-backed refresh is preferred.
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

function getCookieValue(key) {
  if (typeof document === 'undefined') return null
  const parts = document.cookie ? document.cookie.split(';') : []
  for (const part of parts) {
    const idx = part.indexOf('=')
    if (idx <= 0) continue
    const name = decodeURIComponent(part.slice(0, idx).trim())
    if (name !== key) continue
    return decodeURIComponent(part.slice(idx + 1).trim())
  }
  return null
}

function getCsrfToken() {
  return getCookieValue(CSRF_COOKIE_KEY)
}

function getRefreshUrl() {
  const baseUrl = apiClient.defaults.baseURL || ''
  return `${baseUrl.replace(/\/$/, '')}${AUTH_REFRESH_PATH}`
}

function persistTokens(token) {
  if (!token) return null
  const accessToken = typeof token === 'string' ? token : token.accessToken

  if (accessToken) {
    setAccessToken(accessToken)
  }
  // Keep refresh token cookie-only; remove old persisted value.
  localStorage.removeItem(REFRESH_TOKEN_KEY)

  return accessToken || null
}

let refreshRequestPromise = null
let lastRefreshAt = 0
const REFRESH_RESULT_GRACE_MS = 5000

export function __resetRefreshStateForTests() {
  refreshRequestPromise = null
  lastRefreshAt = 0
  clearSessionTokens()
}

export async function refreshAuthTokensSingleFlight() {
  if (refreshRequestPromise) {
    return refreshRequestPromise
  }

  // If another request already refreshed just now, reuse the new access token.
  if (Date.now() - lastRefreshAt <= REFRESH_RESULT_GRACE_MS) {
    const recentToken = getAccessToken()
    if (recentToken) {
      return recentToken
    }
  }

  const refreshToken = getRefreshToken()
  const csrfToken = getCsrfToken()
  const body = refreshToken ? { refreshToken } : {}

  refreshRequestPromise = (async () => {
    const refreshUrl = getRefreshUrl()
    const refreshResponse = await fetch(refreshUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      body: JSON.stringify(body),
    })
    if (!refreshResponse.ok) {
      throw new Error('Refresh failed')
    }

    const data = await refreshResponse.json()
    const token = data?.data?.token || data?.token
    const accessToken = persistTokens(token)
    if (!accessToken) {
      throw new Error('Access token missing from refresh response')
    }
    lastRefreshAt = Date.now()
    return accessToken
  })()

  try {
    return await refreshRequestPromise
  } finally {
    refreshRequestPromise = null
  }
}

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  let token = getAccessToken()
  const locale = localStorage.getItem('app_locale') || 'en'

  if (token === '[object Object]') {
    clearSessionTokens()
    token = null
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const csrfToken = getCsrfToken()
  const method = String(config.method || '').toLowerCase()
  if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(method)) {
    config.headers['X-CSRF-Token'] = csrfToken
  }
  config.headers['X-App-Locale'] = locale

  if (import.meta.env.DEV) {
    console.debug('[apiClient] request:', config.method?.toUpperCase(), config.url, 'token:', !!token)
  }

  return config
})

// Handle responses
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error
    if (import.meta.env.DEV) {
      console.warn('[apiClient] response error', response?.status, config?.url)
    }

    if (response?.status === 401 && config && !config._retry && !isAuthRoute(config.url)) {
      config._retry = true
      try {
        const accessToken = await refreshAuthTokensSingleFlight()
        if (config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`
        } else {
          config.headers = { Authorization: `Bearer ${accessToken}` }
        }
        return apiClient.request(config)
      } catch (refreshError) {
        if (import.meta.env.DEV) {
          console.warn('[apiClient] refresh failed', refreshError)
        }
      }
    }

    if (response?.status === 401) {
      clearSessionTokens()

      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)


export default apiClient

export async function checkQrReceiptDuplicate(qrData) {
  const payload = { qrData }
  const response = await apiClient.post('/receipts/qrcode/preflight', payload)
  return response.data
}
